import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExcursions } from '@/hooks/useExcursions';
import { useAnalytics } from '@/hooks/useAnalytics';

const contactSchema = z.object({
  name: z.string().trim().min(2, 'Le nom doit contenir au moins 2 caractères').max(100, 'Le nom ne peut pas dépasser 100 caractères'),
  email: z.string().trim().email('Email invalide').max(255, 'L\'email ne peut pas dépasser 255 caractères'),
  whatsapp: z.string().trim().max(20, 'Le numéro WhatsApp ne peut pas dépasser 20 caractères').optional(),
  excursion: z.string().min(1, 'Veuillez sélectionner une excursion'),
  date: z.string().min(1, 'Veuillez sélectionner une date'),
  participants: z.string().min(1, 'Veuillez sélectionner le nombre de participants'),
  message: z.string().trim().max(1000, 'Le message ne peut pas dépasser 1000 caractères').optional(),
  consent: z.boolean().refine(val => val === true, 'Vous devez accepter les conditions')
});

type ContactFormData = z.infer<typeof contactSchema>;

export const ContactForm = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { excursions, loading } = useExcursions();
  const { trackContactSubmit } = useAnalytics();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema)
  });

  // Pré-remplir l'excursion si passée en paramètre URL
  useEffect(() => {
    const excursionParam = searchParams.get('excursion');
    if (excursionParam) {
      setValue('excursion', excursionParam);
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: ContactFormData) => {
    try {
      const message = `Bonjour Cléa, je souhaite réserver ${data.excursion} le ${data.date} pour ${data.participants} personnes. Mon nom : ${data.name}. ${data.message ? `Message : ${data.message}` : ''} Merci !`;
      const whatsappUrl = `https://wa.me/5521976795050?text=${encodeURIComponent(message)}`;
      
      trackContactSubmit(data.excursion, 'whatsapp');
      
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: "Demande envoyée",
        description: "Votre demande a été transmise via WhatsApp"
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur s'est produite lors de l'envoi de votre demande"
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-gradient-to-r from-roca-orange/10 to-roca-orange/5 border border-roca-orange/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-roca-orange mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-foreground mb-1">Pourquoi WhatsApp ?</p>
            <p className="text-muted-foreground">
              Nous utilisons WhatsApp pour faciliter les échanges internationaux sans frais supplémentaires. 
              Pas de coûts d'appel ou de SMS, juste une connexion internet. Simple, rapide et économique pour vous !
            </p>
          </div>
        </div>
      </div>

      <div className="bg-muted/50 border border-border rounded-lg p-3">
        <p className="text-xs text-muted-foreground">
          💡 <strong>Conseil :</strong> Si WhatsApp ne s'ouvre pas automatiquement, essayez avec un autre navigateur (Chrome, Firefox, Safari) 
          ou vérifiez que l'application WhatsApp est installée sur votre appareil.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-base mb-2">Nom complet *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Jean Dupont"
            className={`h-12 text-base ${errors.name ? 'border-destructive' : ''}`}
          />
          {errors.name && (
            <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
          )}
        </div>
        <div>
          <Label htmlFor="email" className="text-base mb-2">Email *</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="jean.dupont@email.com"
            className={`h-12 text-base ${errors.email ? 'border-destructive' : ''}`}
          />
          {errors.email && (
            <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
          )}
        </div>
      </div>
      
      <div>
        <Label htmlFor="whatsapp" className="text-base mb-2">WhatsApp</Label>
        <Input
          id="whatsapp"
          {...register('whatsapp')}
          placeholder="+33 6 12 34 56 78"
          className={`h-12 text-base ${errors.whatsapp ? 'border-destructive' : ''}`}
        />
        {errors.whatsapp && (
          <p className="text-sm text-destructive mt-1">{errors.whatsapp.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="excursion" className="text-base mb-2">Excursion souhaitée *</Label>
        <Select onValueChange={(value) => setValue('excursion', value)} disabled={loading}>
          <SelectTrigger className={`h-12 text-base ${errors.excursion ? 'border-destructive' : ''}`}>
            <SelectValue placeholder={loading ? "Chargement..." : "Choisissez votre aventure"} />
          </SelectTrigger>
          <SelectContent>
            {excursions
              ?.filter(exc => exc.actif && exc.is_published)
              .sort((a, b) => (a.ordre || 0) - (b.ordre || 0))
              .map(excursion => (
                <SelectItem key={excursion.id} value={excursion.title}>
                  {excursion.title}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {errors.excursion && (
          <p className="text-sm text-destructive mt-1">{errors.excursion.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="date" className="text-base mb-2">Date souhaitée *</Label>
          <Input
            id="date"
            type="date"
            {...register('date')}
            className={`h-12 text-base ${errors.date ? 'border-destructive' : ''}`}
          />
          {errors.date && (
            <p className="text-sm text-destructive mt-1">{errors.date.message}</p>
          )}
        </div>
        <div>
          <Label htmlFor="participants" className="text-base mb-2">Nombre de personnes *</Label>
          <Select onValueChange={(value) => setValue('participants', value)}>
            <SelectTrigger className={`h-12 text-base ${errors.participants ? 'border-destructive' : ''}`}>
              <SelectValue placeholder="Combien serez-vous ?" />
            </SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6,7,8].map(nb => (
                <SelectItem key={nb} value={nb.toString()}>
                  {nb} personne{nb > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.participants && (
            <p className="text-sm text-destructive mt-1">{errors.participants.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="message" className="text-base mb-2">Informations complémentaires (optionnel)</Label>
        <Textarea
          id="message"
          {...register('message')}
          rows={4}
          placeholder="Précisions sur vos attentes, questions particulières, demandes spéciales..."
          className={`text-base ${errors.message ? 'border-destructive' : ''}`}
        />
        {errors.message && (
          <p className="text-sm text-destructive mt-1">{errors.message.message}</p>
        )}
      </div>

      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          id="consent"
          {...register('consent')}
          className="mt-1 cursor-pointer"
        />
        <Label htmlFor="consent" className="text-sm text-muted-foreground cursor-pointer">
          J'accepte que mes données personnelles soient utilisées pour traiter ma demande de réservation conformément à la{" "}
          <a href="/confidentialite" className="text-roca-red hover:underline font-medium">
            politique de confidentialité
          </a>
          {" "}*
        </Label>
        {errors.consent && (
          <p className="text-sm text-destructive mt-1">{errors.consent.message}</p>
        )}
      </div>

      <Button 
        type="submit" 
        disabled={isSubmitting}
        size="lg"
        className="w-full bg-green-600 hover:bg-green-700 text-white text-lg h-14 md:h-16 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
      >
        <MessageCircle className="w-6 h-6 mr-3" />
        <div className="flex flex-col items-start">
          <span className="font-bold">{isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}</span>
          <span className="text-xs opacity-90">Via WhatsApp • Réponse sous 24h</span>
        </div>
      </Button>
      
      <p className="text-xs text-center text-muted-foreground">
        En cliquant, vous serez redirigé vers WhatsApp avec votre message pré-rempli
      </p>
      
    </form>
  );
};